// src/api/tds.ts
//
// Salary TDS (section 192) — challans, deductees and the quarterly 24Q return.
// The backend derives every "is this step done" answer from stored facts rather
// than from flags, so the page reads /tds/quarter once and renders from it.
//
// Design: hrms_prod/docs/superpowers/specs/2026-09-30-salary-tds-24q-design.md
import api from "./api";

export interface TdsChallan {
  id: string;
  financial_year: string;
  quarter: number;
  month: number;
  section: string;
  // Every head the portal is paid for. A challan of 18,000 tax and 540
  // interest is a challan of 18,540, and anything that adds up only the
  // first four shows the wrong number.
  tds_amount: number;
  surcharge_amount: number;
  edu_cess_amount: number;
  higher_edu_cess_amount: number;
  interest_amount: number;
  penalty_amount: number;
  late_fee_amount: number;
  other_amount: number;
  // Assigned when the challan is generated on the portal, before any money
  // moves — so a CRN on its own does not mean it was paid.
  crn: string | null;
  cin: string | null;
  alternate_cin: string | null;
  bsr_code: string | null;
  challan_serial_no: string | null;
  deposit_date: string | null;
  bank_name: string | null;
  bank_reference_no: string | null;
  status: "draft" | "paid";
  due_date: string | null;
}

export interface TdsDeductee {
  id: string;
  challan_id: string;
  emptID: string;
  employee_name: string | null;
  pan: string | null;
  gross_amount: number;
  tds_amount: number;
}

export interface NotApprovedInfo {
  records: number;
  net: number;
}

// Why a month cannot go into the return yet. Kept as codes so the UI owns the
// wording and the backend owns the rule.
export type TdsBlocker =
  | "payroll_not_approved"
  | "challan_not_paid"
  | "challan_reference_missing"
  | "pan_missing";

export interface TdsMonth {
  month: number;
  year: number;
  settled: boolean;
  blockers: TdsBlocker[];
  tds_total: number;
  deductee_count: number;
  missing_pan: string[];
  not_approved: NotApprovedInfo;
  deposited: number;
  challans: TdsChallan[];
  due_date: string;
}

export interface TdsReturn {
  id: string;
  financial_year: string;
  quarter: number;
  form: string;
  filed_date: string | null;
  acknowledgement_no: string | null;
  status: string;
  due_date: string | null;
}

export interface TdsQuarter {
  financial_year: string;
  quarter: number;
  months: TdsMonth[];
  quarter_ready: boolean;
  return: TdsReturn | null;
  return_due_date: string;
}

export interface TdsDeducteesResponse {
  year: number;
  month: number;
  financial_year: string;
  deductees: Array<{
    emptID: string;
    employee_name: string | null;
    pan: string | null;
    gross_amount: number;
    tds_amount: number;
  }>;
  tds_total: number;
  missing_pan: string[];
  not_approved: NotApprovedInfo;
}

export interface TdsReturnData {
  financial_year: string;
  quarter: number;
  challans: Array<{ challan: TdsChallan; deductees: TdsDeductee[] }>;
}

/** A salary-TDS transaction as the template carries it. Stored, not derived
 *  from payroll -- which is why these can be edited and deleted. */
export interface TdsSalaryEntry {
  id: string;
  employee_code: string | null;
  employee_name: string;
  pan: string | null;
  paid_on: string | null;
  /** 24Q reports both; the template asks for one and fills both. */
  deducted_on: string | null;
  section: string;
  /** "resident" or "nonResident". The portal takes a SEPARATE challan for
   *  each, so the two cannot be deposited together. */
  deductee_type: string;
  amount_paid: number;
  tds: number;
  surcharge: number;
  education_cess: number;
  health_cess: number;
  total: number;
  /** A Yes/No in the template and a flag in the 24Q, not an amount: did
   *  the EMPLOYER bear the tax on a non-monetary perquisite. */
  us_192_1a: boolean;
  source: string;
  source_file: string | null;
  /** Which challan reports this row. Worked out on read, never stored. */
  challan: {
    id: string;
    status: string;
    month: number | null;
    bsr_code: string | null;
    challan_serial_no: string | null;
    deposit_date: string | null;
  } | null;
  deposited: boolean;
}

export interface TdsSalaryEntriesResponse {
  financial_year: string;
  quarter: number;
  entries: TdsSalaryEntry[];
  totals: { amount_paid: number; tds: number; total: number;
            deposited: number; pending: number };
  /** The template's headings, straight from the server that builds it. */
  columns: string[];
}

/** What an edit sends. The challan and the deposited flag are DERIVED on
 *  read, so they are not the caller's to set — leaving them in would offer
 *  a form field that the server ignores. */
export type TdsSalaryEntryInput =
  Omit<TdsSalaryEntry, "id" | "source" | "source_file" | "challan" | "deposited">;

/** Interest is worked out MONTH BY MONTH: a quarter deposited in one go is
 *  not three months of interest on the whole sum — July's tax has been held
 *  since July and September's since September. */
export interface ChallanSuggestion {
  month: number | null;
  months_covered: number[];
  entries: number;
  due: number;
  already_paid: number;
  pending: number;
  deposit_due_dates: Array<{ month: number; due: string }>;
  interest: {
    amount: number; rate: number; late: boolean;
    /** One per deduction DATE: 201(1A) counts date to date, so the day
     *  inside the month changes the answer and two deductions in one
     *  July are not one line. */
    lines: Array<{
      deducted: string;
      year: number; month: number; amount: number;
      months: number; interest: number; due: string; late: boolean;
    }>;
  };
  statement_due_date: string | null;
  late_fee: { amount: number; days: number; capped: boolean };
}

/** What an upload did: the rows it added, and the rows it took the place
 *  of. A replace is reported rather than silent -- it is scoped to the
 *  months the file covers, and the filer should be told which those were. */
export interface ImportResult {
  imported: number;
  replaced: number;
  /** How many of the replaced rows had been corrected by hand. */
  replaced_edited: number;
  /** The months the file spoke for, e.g. "July 2026 and August 2026". */
  months: string;
  entries: TdsSalaryEntry[];
}

/** One payment as the e-filing portal reports it, plus the challan here it
 *  could be recorded onto. Nothing in this is a challan: it is a copy of
 *  the department's record, and reconciling is writing a BSR code onto a
 *  challan the branch actually raised. */
export interface PortalChallanRow {
  id: string;
  cin: string | null;
  crn: string | null;
  minor_head: string | null;
  section: string | null;
  tender_date: string | null;
  challan_number: string | null;
  bsr_code: string | null;
  bank_name: string | null;
  tds_amount: number;
  surcharge_amount: number;
  edu_cess_amount: number;
  interest_amount: number;
  penalty_amount: number;
  late_fee_amount: number;
  other_amount: number;
  total_amount: number;
  fetched_at: string | null;
  /** The one challan here this could be, or null when it is ambiguous or
   *  there is none. Worked out on every read, never stored. */
  match: {
    id: string; month: number | null; section: string;
    total: number; status: string;
  } | null;
  /** Both the BSR code and the serial are there -- the two things a 24Q
   *  cannot be filed without, and so the two worth recording. */
  has_receipt: boolean;
}

export const tdsApi = {
  // ---- salary entries: the transactions brought in from the template -----

  /** Everyone this branch has ever entered a row for, newest value of each
   *  field. Used to fill a row in rather than retyping a PAN. */
  salaryEmployees: () =>
    api.get<{ employees: Array<{
      employee_code: string | null; employee_name: string | null;
      pan: string | null; last_entry_date: string | null; entry_count: number;
    }> }>(`/tds/salary-entries/employees`),

  salaryEntries: (financial_year: string, quarter: number) =>
    api.get<TdsSalaryEntriesResponse>(`/tds/salary-entries`, { params: { financial_year, quarter } }),

  // The blank template. Built server-side from the same column list the
  // importer reads, so what is handed out and what is accepted cannot drift.
  downloadSalaryTemplate: async (financial_year: string, quarter: number) => {
    const res = await api.get(`/tds/salary-entries/template`, {
      params: { financial_year, quarter },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data as Blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `tds-salary-template-${financial_year}-Q${quarter}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  // The quarter's rows in the template's own format, so an export can be
  // corrected and imported straight back.
  //
  // Narrowed by the same month and section the screen is, because a file
  // that holds rows the table was not showing is one somebody will correct
  // and import back without ever having looked at them.
  downloadSalaryEntries: async (
    financial_year: string, quarter: number,
    scope: { month?: number; section?: string } = {},
  ) => {
    const res = await api.get(`/tds/salary-entries/export`, {
      params: { financial_year, quarter, ...scope },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data as Blob);
    const link = document.createElement("a");
    const part = [scope.month ? `-M${scope.month}` : "", scope.section ? `-${scope.section}` : ""].join("");
    link.href = url;
    link.download = `tds-salary-entries-${financial_year}-Q${quarter}${part}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  // The workbook goes up as bytes and is parsed on the server -- one reader,
  // the same one that built the file.
  importSalaryEntries: (payload: {
    financial_year: string; quarter: number;
    file_name?: string; data_base64: string; replace?: boolean;
  }) => api.post<ImportResult>(`/tds/salary-entries/import`, payload),

  updateSalaryEntry: (id: string, payload: TdsSalaryEntryInput) =>
    api.patch<{ entry: TdsSalaryEntry }>(`/tds/salary-entries/${id}`, payload),

  deleteSalaryEntry: (id: string) =>
    api.delete<{ deleted: string }>(`/tds/salary-entries/${id}`),

  // Remove a challan that should not be there. HARD -- the server refuses
  // one that carries its receipt, because that is the only record the
  // return maps its deductees to.
  deleteChallan: (challanId: string) =>
    api.delete<{ deleted: string }>(`/tds/challan/${challanId}`),

  // What the portal says it was paid, for this quarter.
  portalChallans: (financial_year: string, quarter: number) =>
    api.get<{ rows: PortalChallanRow[] }>(`/tds/portal-challans`,
      { params: { financial_year, quarter } }),

  // Write one portal row's BSR code and serial onto the challan it matches.
  // The server works the match out again rather than trusting the screen:
  // this page may have been open a while, and what it decided then is not
  // evidence now.
  recordPortalChallan: (rowId: string) =>
    api.post<{ challan: TdsChallan; updated: string[] }>(
      `/tds/portal-challans/${rowId}/record`),

  // `return_type` picks which 24Q the stepper is about: the regular one,
  // or a correction filed after it. They are separate returns for one
  // quarter, so the filing step has to say which it means.
  quarter: (financial_year: string, quarter: number, return_type = "regular") =>
    api.get<TdsQuarter>(`/tds/quarter`, { params: { financial_year, quarter, return_type } }),

  deductees: (year: number, month: number) =>
    api.get<TdsDeducteesResponse>(`/tds/deductees`, { params: { year, month } }),

  // Snapshots the month's deductees onto the new challan. `tds_amount` is
  // optional — omitted, the month's own approved total is used.
  /** What a new challan should start out saying: the month's unpaid tax,
   *  interest under 201(1A) once a deposit date is given, and the 234E fee
   *  once the statement is overdue. Suggestions — all of them editable. */
  suggestChallan: (params: {
    financial_year: string; quarter: number;
    /** Left out for one challan covering the whole quarter. */
    month?: number;
    section?: string; deposit_date?: string;
  }) => api.get<ChallanSuggestion>(`/tds/challan/suggest`, { params }),

  createChallan: (payload: {
    year: number;
    /** Left out for a challan covering the whole quarter; then the
     *  financial year and quarter are required instead. */
    month?: number;
    financial_year?: string;
    quarter?: number;
    section?: string;
    tds_amount?: number;
    interest_amount?: number;
    surcharge_amount?: number;
    edu_cess_amount?: number;
    higher_edu_cess_amount?: number;
    penalty_amount?: number;
    late_fee_amount?: number;
    other_amount?: number;
    /** "200" payable by the taxpayer, "400" raised by the department. */
    payment_type?: string;
    deposit_date?: string | null;
  }) =>
    api.post<{ challan: TdsChallan; deductees: number }>(`/tds/challan`, payload),

  // Manual correction. Unlike the extension's receipt write, this MAY overwrite
  // a value that is already there.
  updateChallan: (
    id: string,
    payload: { bsr_code?: string; challan_serial_no?: string; deposit_date?: string },
  ) => api.patch<{ challan: TdsChallan }>(`/tds/challan/${id}`, payload),

  challanDeductees: (id: string) =>
    api.get<{ deductees: TdsDeductee[] }>(`/tds/challan/${id}/deductees`),

  markReturnFiled: (payload: {
    financial_year: string;
    quarter: number;
    filed_date?: string;
    acknowledgement_no?: string;
    status?: string;
  }) => api.post<{ return: TdsReturn }>(`/tds/return`, payload),

  returnData: (financial_year: string, quarter: number) =>
    api.get<TdsReturnData>(`/tds/return-data`, { params: { financial_year, quarter } }),
};
